import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Summary Card Component
 * Displays a metric with optional trend indicator
 */
const SummaryCard = ({
  title,
  value,
  subtitle,
  trend,
  trendValue,
  icon: Icon,
  isCurrency = false,
  isPercentage = false,
  className = ''
}) => {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-green-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-muted-foreground';
  };

  const formatValue = (val, isCurrency = false, isPercentage = false) => {
    if (typeof val === 'number') {
      if (isPercentage) {
        // Format percentage values
        return `${val.toFixed(2)}%`;
      } else if (isCurrency) {
        // Format currency values
        if (val >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
        if (val >= 1000) return `$${(val / 1000).toFixed(2)}K`;
        return `$${val.toFixed(2)}`;
      } else {
        // Format non-currency numbers
        if (val >= 1000000) return `${(val / 1000000).toFixed(2)}M`;
        if (val >= 1000) return `${(val / 1000).toFixed(2)}K`;
        return val.toLocaleString();
      }
    }
    return val;
  };

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-2 px-2 sm:px-3">
        <CardTitle className="text-xs font-medium text-muted-foreground truncate pr-1">{title}</CardTitle>
        {Icon && <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground flex-shrink-0" />}
      </CardHeader>
      <CardContent className="px-2 sm:px-3 pb-2 pt-0">
        <div className="text-base sm:text-lg lg:text-xl font-bold">{formatValue(value, isCurrency, isPercentage)}</div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>
        )}
        {trend && trendValue !== undefined && (
          <div className={`flex items-center gap-1 mt-1 ${getTrendColor()}`}>
            {getTrendIcon()}
            <span className="text-xs font-medium">
              {trendValue > 0 ? '+' : ''}{trendValue.toFixed(1)}%
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SummaryCard;
